import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Building2, Calculator, Download, Clock, Package, AlertTriangle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { companyService } from '@/services/companyService';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import * as XLSX from 'xlsx';
import { decodeHtmlEntities } from '@/lib/utils';

const PURCHASE_CATEGORY: CategoryKey = 'purchase';

interface PurchaseSuggestion {
  productId: string;
  brand: string;
  productName: string;
  cost: number;
  warehouse: string;
  status: string;
  currentStock: number;
  safetyStock: number;
  suggestedQuantity: number;
}

interface CalculationResult {
  suggestions: PurchaseSuggestion[];
  calculationTime: Date;
  totalProducts: number;
}

// Convert service level percentage to statistical Z-score
const getZScoreFromServiceLevel = (percentage: number): number => {
  // High service levels
  if (percentage >= 99.9) return 3.09; // 99.9%
  if (percentage >= 99.8) return 2.88; // 99.8%
  if (percentage >= 99) return 2.33;   // 99%
  if (percentage >= 98) return 2.05;   // 98%
  if (percentage >= 95) return 1.645;  // 95%

  // Medium-high service levels (every 5%)
  if (percentage >= 90) return 1.28;   // 90%
  if (percentage >= 85) return 1.04;   // 85%
  if (percentage >= 80) return 0.84;   // 80%
  if (percentage >= 75) return 0.67;   // 75%
  if (percentage >= 70) return 0.52;   // 70%
  if (percentage >= 65) return 0.38;   // 65%
  if (percentage >= 60) return 0.25;   // 60%
  if (percentage >= 55) return 0.13;   // 55%

  // For very low service levels, use small positive values to avoid zero safety stock
  // Even low service levels need some statistical safety stock for demand variability
  if (percentage >= 45) return 0.005;  // 45% - very small positive
  if (percentage >= 40) return 0.01;   // 40% - very small positive
  if (percentage >= 35) return 0.005;  // 35% - very small positive
  if (percentage >= 30) return 0.01;   // 30% - very small positive
  if (percentage >= 25) return 0.005;  // 25% - very small positive
  if (percentage >= 20) return 0.01;   // 20% - very small positive
  if (percentage >= 15) return 0.005;  // 15% - very small positive
  if (percentage >= 10) return 0.01;   // 10% - very small positive
  if (percentage >= 5) return 0.005;   // 5% - very small positive
  if (percentage >= 1) return 0.01;    // 1% - very small positive
  if (percentage >= 0) return 0.005;   // 0% - very small positive

  // Default fallback (should not happen)
  return 1.645;
};

const CrossCompanySuggestions = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Search filters
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ONLINE'); // Default: ONLINE
  const [skuId, setSkuId] = useState('');
  const [productName, setProductName] = useState('');
  const [resultFilter, setResultFilter] = useState('purchase_needed'); // Default: purchase_needed

  // Data states
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);

  // Progress tracking
  const [progress, setProgress] = useState({
    totalProducts: 0,
    processedProducts: 0,
    currentProduct: '',
  });

  // Load brand options on component mount
  useEffect(() => {
    loadBrandOptions();
  }, []);

  const loadBrandOptions = async () => {
    try {
      await ensureAuthReady();
      
      // 🔄 改动点 1: 获取用户所有授权的公司
      const userCompanies = await companyService.getUserCompanies();
      if (!userCompanies || userCompanies.length === 0) {
        console.log('User has no authorized companies');
        return;
      }
      const companyIds = userCompanies.map(c => c.id);

      const { data: brands, error } = await supabase
        .from('products')
        .select('brand')
        .in('company_id', companyIds)  // 🔄 改动: 使用 in 查询多个公司
        .not('brand', 'is', null)
        .order('brand');

      if (error) {
        console.error('Error loading brands:', error);
        return;
      }

      const uniqueBrands = [...new Set(brands.map(b => b.brand).filter(Boolean))];
      setBrandOptions(uniqueBrands);
    } catch (error) {
      console.error('Error in loadBrandOptions:', error);
    }
  };

  const calculateSafetyStock = async () => {
    try {
      setCalculating(true);
      await ensureAuthReady();

      // 🔄 改动点 2: 获取用户所有授权的公司
      const userCompanies = await companyService.getUserCompanies();
      if (!userCompanies || userCompanies.length === 0) {
        toast.error(language === 'zh' ? '未找到授權公司' : 'No authorized companies found');
        return;
      }
      const companyIds = userCompanies.map(c => c.id);

      // 获取当前公司（用于 purchase_rules）
      const currentCompany = await companyService.getCurrentCompany();
      if (!currentCompany) {
        toast.error(language === 'zh' ? '請先選擇公司' : 'Please select a company first');
        return;
      }

      // Initialize progress
      setProgress({
        totalProducts: 0,
        processedProducts: 0,
        currentProduct: language === 'zh' ? '準備計算...' : 'Preparing calculation...',
      });

      // Get purchase rules (使用当前公司的规则)
      const { data: rules, error: rulesError } = await supabase
        .from('purchase_rules')
        .select('*')
        .eq('company_id', currentCompany.id)
        .eq('is_active', true);

      if (rulesError) {
        console.error('Error loading rules:', rulesError);
        toast.error(language === 'zh' ? '載入規則設定失敗' : 'Failed to load rule settings');
        return;
      }

      if (!rules || rules.length === 0) {
        toast.error(language === 'zh' ? '請先設定採購規則' : 'Please configure purchase rules first');
        return;
      }

      // Parse rules
      const ruleValues: { [key: string]: number } = {};
      rules.forEach(rule => {
        ruleValues[rule.rule_key] = parseFloat(rule.rule_value) || 0;
      });

      // Convert service level percentage to Z-score
      const serviceLevelPercent = ruleValues.service_level || 95;
      const Z = getZScoreFromServiceLevel(serviceLevelPercent); // Service level factor
      const L = ruleValues.lead_time || 7; // Lead time
      const sigma_L = ruleValues.lead_time_std || 1; // Lead time standard deviation
      const bufferDays = ruleValues.buffer_days || 21; // Buffer stock days

      // 🔄 改动点 2: 查询所有授权公司的产品
      let productsQuery = supabase
        .from('products')
        .select(`
          company_sku_key,
          company_id,
          sku_id,
          sku_name,
          sku_name_chi,
          sku_status,
          brand,
          warehouse
        `)
        .in('company_id', companyIds);  // 🔄 改动: 使用 in 查询多个公司

      if (brandFilter) {
        productsQuery = productsQuery.eq('brand', brandFilter);
      }

      if (statusFilter !== 'all') {
        productsQuery = productsQuery.eq('sku_status', statusFilter);
      }

      if (skuId) {
        productsQuery = productsQuery.ilike('sku_id', `%${skuId}%`);
      }

      if (productName) {
        productsQuery = productsQuery.or(`sku_name.ilike.%${productName}%,sku_name_chi.ilike.%${productName}%`);
      }

      const { data: products, error: productsError } = await productsQuery;

      if (productsError) {
        console.error('Error loading products:', productsError);
        console.error('Query details:', {
          companyIds,
          brandFilter,
          skuId,
          productName,
          statusFilter
        });
        toast.error(language === 'zh' ? `載入產品數據失敗: ${productsError.message}` : `Failed to load product data: ${productsError.message}`);
        return;
      }

      if (!products || products.length === 0) {
        setCalculationResult({
          suggestions: [],
          calculationTime: new Date(),
          totalProducts: 0
        });
        toast.info(language === 'zh' ? '沒有找到符合條件的產品' : 'No products found matching the criteria');
        return;
      }

      // Set total products for progress tracking
      setProgress(prev => ({
        ...prev,
        totalProducts: products.length,
        currentProduct: language === 'zh' ? `開始計算 ${products.length} 個產品...` : `Starting calculation of ${products.length} products...`,
      }));

      // Calculate suggestions for each product
      const suggestions: PurchaseSuggestion[] = [];

      for (const product of products) {
        // Update progress for current product
        setProgress(prev => ({
          ...prev,
          processedProducts: prev.processedProducts + 1,
          currentProduct: language === 'zh'
            ? `計算中: ${product.sku_name || product.sku_name_chi || product.sku_id}`
            : `Calculating: ${product.sku_name || product.sku_name_chi || product.sku_id}`,
        }));

        try {
          // Get product cost first
          const { data: costData, error: costError } = await supabase
            .from('product_costs')
            .select('average_cost')
            .eq('company_sku_key', product.company_sku_key)
            .single();

          const productCost = costData?.average_cost || 0;

          if (costError && costError.code !== 'PGRST116') {
            console.error('Error loading cost for product:', product.sku_id, costError);
          }

          // Get sales data for the last 90 days
          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

          // Format date as YYYY-MM-DD for database query
          const ninetyDaysAgoStr = ninetyDaysAgo.toISOString().split('T')[0];

          console.log('Querying sales data for product:', product.sku_id, 'from date:', ninetyDaysAgoStr);

          const { data: salesData, error: salesError } = await supabase
            .from('sales_orders')
            .select('sale_quantity, order_date')
            .eq('company_sku_key', product.company_sku_key)
            .gte('order_date', ninetyDaysAgoStr);

          if (salesError) {
            console.error('Error loading sales data for product:', product.sku_id, salesError);
            continue;
          }

          // Calculate average daily demand (d_avg) - 正確實現
          // 公式：d_avg = 總銷售量 ÷ 觀察期天數 (90天)

          // 1. 計算總銷售量
          const totalSalesQuantity = salesData?.reduce((sum, sale) =>
            sum + Number(sale.sale_quantity || 0), 0) || 0;

          // 2. 觀察期固定為90天
          const observationPeriod = 90;

          // 3. 計算平均日需求
          const d_avg = totalSalesQuantity / observationPeriod;

          // Calculate demand standard deviation (σ_d)
          // 考慮所有90天，包括無銷售天數（視為0）
          const dailySalesMap = new Map<string, number>();

          // 按日期分組，計算每天的銷售總量
          salesData?.forEach(sale => {
            const saleDate = sale.order_date.split('T')[0]; // 取日期部分
            const currentAmount = dailySalesMap.get(saleDate) || 0;
            dailySalesMap.set(saleDate, currentAmount + Number(sale.sale_quantity || 0));
          });

          // 創建90天的完整數據，無銷售天數視為0
          const allDays: number[] = [];
          const startDate = new Date(ninetyDaysAgo);

          for (let i = 0; i < observationPeriod; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            const dateStr = currentDate.toISOString().split('T')[0];
            const salesAmount = dailySalesMap.get(dateStr) || 0;
            allDays.push(salesAmount);
          }

          // 計算樣本標準差 (基於所有90天數據)
          let sigma_d = 0;
          if (allDays.length > 1) {
            const variance = allDays.reduce((sum, amount) => {
              const diff = amount - d_avg;
              return sum + diff * diff;
            }, 0) / (allDays.length - 1); // 樣本標準差
            sigma_d = Math.sqrt(variance);
          }

          // Calculate safety stock: S = d_avg × (L + buffer_days) + SS
          // where SS = Z × √(L × σ_d² + d_avg² × σ_L²)
          const statisticalSS = Z * Math.sqrt(L * sigma_d * sigma_d + d_avg * d_avg * sigma_L * sigma_L);
          const safetyStock = d_avg * (L + bufferDays) + statisticalSS; // 正確公式：補至上限

          // Debug logging for products with high safety stock or unusual calculations
          const salesDataCount = salesData?.length || 0;
          if (safetyStock > 50 || (salesDataCount > 0 && d_avg < 0.5 && safetyStock > 10)) {
            console.log(`Safety stock calculation for ${product.sku_id}:`, {
              salesDataCount,
              dailyAmountsCount: allDays.length,
              d_avg: d_avg.toFixed(3),
              sigma_d: sigma_d.toFixed(3),
              serviceLevelPercent,
              Z: Z.toFixed(3),
              L,
              sigma_L,
              bufferDays,
              statisticalSS: statisticalSS.toFixed(1),
              finalSafetyStock: safetyStock
            });
          }

          // Get current inventory
          const { data: inventoryData, error: inventoryError } = await supabase
            .from('product_inventory')
            .select('merchant_inventory_qty')
            .eq('company_sku_key', product.company_sku_key)
            .single();

          if (inventoryError && inventoryError.code !== 'PGRST116') { // PGRST116 is "not found"
            console.error('Error loading inventory for product:', product.sku_id, inventoryError);
            continue;
          }

          const currentStock = inventoryData?.merchant_inventory_qty || 0;

          // Calculate suggested quantity: SS - current stock
          const suggestedQuantity = safetyStock - currentStock;

          // Apply result filter
          let shouldInclude = true;
          if (resultFilter === 'purchase_needed') {
            shouldInclude = suggestedQuantity > 0;
          } else if (resultFilter === 'no_purchase_needed') {
            shouldInclude = suggestedQuantity <= 0;
          }
          // 'all' includes everything

          if (shouldInclude) {
            suggestions.push({
              productId: product.sku_id,
              brand: product.brand || '-',
              productName: decodeHtmlEntities(language === 'zh' ? (product.sku_name_chi || product.sku_name || '') : (product.sku_name || product.sku_name_chi || '')),
              cost: productCost,
              warehouse: product.warehouse || 'MAIN', // Use actual warehouse from products table
              status: product.sku_status || 'OFFLINE',
              currentStock,
              safetyStock,
              suggestedQuantity
            });
          }
        } catch (error) {
          console.error('Error calculating for product:', product.sku_id, error);
          continue;
        }
      }

      // Sort by brand name
      suggestions.sort((a, b) => a.brand.localeCompare(b.brand));

      const result: CalculationResult = {
        suggestions,
        calculationTime: new Date(),
        totalProducts: suggestions.length
      };

      setCalculationResult(result);

      // Reset progress
      setProgress({
        totalProducts: 0,
        processedProducts: 0,
        currentProduct: '',
      });

      if (suggestions.length === 0) {
        toast.info(language === 'zh' ? '沒有找到需要採購的產品' : 'No products found that need purchasing');
      } else {
        toast.success(language === 'zh'
          ? `計算完成，共 ${suggestions.length} 個產品`
          : `Calculation completed, ${suggestions.length} products found`);
      }

    } catch (error) {
      console.error('Error in calculateSafetyStock:', error);
      toast.error(language === 'zh' ? '計算過程中發生錯誤' : 'Error occurred during calculation');
    } finally {
      setCalculating(false);
    }
  };

  const exportToExcel = () => {
    if (!calculationResult || calculationResult.suggestions.length === 0) {
      toast.error(language === 'zh' ? '沒有數據可匯出' : 'No data to export');
      return;
    }

    try {
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();

      // Prepare data for Excel
      const headers = language === 'zh'
        ? ['產品ID', '品牌', '產品名稱', '成本', '倉庫', '狀態', '現存量', '安全量', '建議採納量']
        : ['Product ID', 'Brand', 'Product Name', 'Cost', 'Warehouse', 'Status', 'Current Stock', 'Safety Stock', 'Suggested Quantity'];

      const rows = calculationResult.suggestions.map(item => [
        item.productId,
        item.brand,
        item.productName,
        item.cost > 0 ? item.cost.toFixed(2) : '-',
        item.warehouse,
        item.status,
        item.currentStock,
        Math.ceil(item.safetyStock),
        Math.ceil(item.suggestedQuantity)
      ]);

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Product ID
        { wch: 12 }, // Brand
        { wch: 30 }, // Product Name
        { wch: 10 }, // Cost
        { wch: 10 }, // Warehouse
        { wch: 10 }, // Status
        { wch: 12 }, // Current Stock
        { wch: 12 }, // Safety Stock
        { wch: 15 }  // Suggested Quantity
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, language === 'zh' ? '跨公司採購建議' : 'Cross-Company Suggestions');

      // Generate Excel file
      const timestamp = calculationResult.calculationTime.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = language === 'zh'
        ? `跨公司採購建議_${timestamp}.xlsx`
        : `Cross_Company_Purchase_Suggestions_${timestamp}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      toast.success(language === 'zh' ? 'Excel 匯出成功' : 'Excel export successful');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error(language === 'zh' ? 'Excel 匯出失敗' : 'Excel export failed');
    }
  };

  const resetFilters = () => {
    setBrandFilter('');
    setStatusFilter('ONLINE');
    setSkuId('');
    setProductName('');
    setResultFilter('purchase_needed');
    setCalculationResult(null);
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                localStorage.setItem('selectedCategory', PURCHASE_CATEGORY);
                navigate(ROUTES.dashboard.root);
              }}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {language === 'zh' ? '返回' : 'Back'}
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-2">
                <Building2 className="w-6 h-6 text-purple-400" />
                {language === 'zh' ? '跨公司採購建議' : 'Cross-Company Purchase Suggestions'}
              </h1>
              <p className="text-gray-400 mt-1">
                {language === 'zh' ? '一次性查看所有授權公司的採購建議' : 'View purchase suggestions for all authorized companies at once'}
              </p>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <Card className="bg-slate-800/80 border-slate-700 mb-6">
          <CardContent className="p-6">
            {/* Search Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  {language === 'zh' ? '產品 ID' : 'SKU ID'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品 ID' : 'Search SKU ID'}
                  value={skuId}
                  onChange={(e) => setSkuId(e.target.value)}
                  className="bg-slate-800/50 border-slate-600 text-slate-200 placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  {language === 'zh' ? '產品名稱' : 'Product Name'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品名稱' : 'Search Product Name'}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="bg-slate-800/50 border-slate-600 text-slate-200 placeholder:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  {language === 'zh' ? '品牌名稱' : 'Brand Name'}
                </label>
                <Select value={brandFilter} onValueChange={(v) => setBrandFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-10 bg-slate-800/50 border-slate-600 text-slate-200 hover:bg-slate-700/50">
                    <SelectValue placeholder={language === 'zh' ? '全部品牌' : 'All Brands'} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900/90 border-slate-700 text-slate-200">
                    <SelectItem value="all">{language === 'zh' ? '全部品牌' : 'All Brands'}</SelectItem>
                    {brandOptions.map((brand) => (
                      <SelectItem key={brand} value={brand} className="focus:bg-slate-700/60 data-[highlighted]:bg-slate-700/60">
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  {language === 'zh' ? '在線狀態' : 'Online Status'}
                </label>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
                  <SelectTrigger className="h-10 bg-slate-800/50 border-slate-600 text-slate-200 hover:bg-slate-700/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900/90 border-slate-700 text-slate-200">
                    <SelectItem value="all">{language === 'zh' ? '所有狀態' : 'All Status'}</SelectItem>
                    <SelectItem value="ONLINE">{language === 'zh' ? '在線' : 'Online'}</SelectItem>
                    <SelectItem value="OFFLINE">{language === 'zh' ? '離線' : 'Offline'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  {language === 'zh' ? '計算結果' : 'Calculation Result'}
                </label>
                <Select value={resultFilter} onValueChange={(v) => setResultFilter(v)}>
                  <SelectTrigger className="h-10 bg-slate-800/50 border-slate-600 text-slate-200 hover:bg-slate-700/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900/90 border-slate-700 text-slate-200">
                    <SelectItem value="purchase_needed">{language === 'zh' ? '需採購' : 'Purchase Needed'}</SelectItem>
                    <SelectItem value="no_purchase_needed">{language === 'zh' ? '不需採購' : 'No Purchase Needed'}</SelectItem>
                    <SelectItem value="all">{language === 'zh' ? '顯示所有結果' : 'Show All Results'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                >
                  {language === 'zh' ? '重置篩選' : 'Reset Filters'}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={calculateSafetyStock}
                  disabled={calculating}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                >
                  {calculating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {language === 'zh' ? '計算中...' : 'Calculating...'}
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      {language === 'zh' ? '計算採購量' : 'Calculate Purchase Quantity'}
                    </>
                  )}
                </Button>
                {calculationResult && calculationResult.suggestions.length > 0 && (
                  <Button
                    onClick={exportToExcel}
                    variant="outline"
                    className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {language === 'zh' ? '匯出Excel' : 'Export Excel'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calculation Info */}
        {calculationResult && (
          <Alert className="mb-6 bg-card/80 border-border">
            <Clock className="h-4 w-4" />
            <AlertDescription className="text-foreground">
              {language === 'zh'
                ? `計算時間: ${calculationResult.calculationTime.toLocaleString('zh-CN')} | 共 ${calculationResult.totalProducts} 個產品`
                : `Calculation Time: ${calculationResult.calculationTime.toLocaleString('en-US')} | Total ${calculationResult.totalProducts} products`
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Results Table */}
        <Card className="bg-card/80 border-border shadow-lg shadow-black/30">
          <CardContent className="p-0">
            {calculating ? (
              <div className="py-12 px-6">
                <div className="flex items-center justify-center mb-4">
                  <Loader2 className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mr-3" />
                  <div className="text-center">
                    <p className="text-muted-foreground text-lg">{language === 'zh' ? '計算中...' : 'Calculating...'}</p>
                    <p className="text-muted-foreground text-sm mt-1">{progress.currentProduct}</p>
                  </div>
                </div>

                {progress.totalProducts > 0 && (
                  <div className="max-w-md mx-auto">
                    <div className="flex justify-between text-sm text-muted-foreground mb-2">
                      <span>
                        {language === 'zh' ? '進度' : 'Progress'}: {progress.processedProducts} / {progress.totalProducts}
                      </span>
                      <span>
                        {Math.round((progress.processedProducts / progress.totalProducts) * 100)}%
                      </span>
                    </div>
                    <Progress
                      value={(progress.processedProducts / progress.totalProducts) * 100}
                      className="h-3 bg-muted"
                    />
                    <p className="text-center text-muted-foreground text-sm mt-2">
                      {language === 'zh'
                        ? `預計還有 ${Math.max(0, progress.totalProducts - progress.processedProducts)} 個產品`
                        : `Estimated ${Math.max(0, progress.totalProducts - progress.processedProducts)} products remaining`
                      }
                    </p>
                  </div>
                )}
              </div>
            ) : !calculationResult ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'zh' ? '請點擊「計算採購量」開始計算' : 'Click "Calculate Purchase Quantity" to start calculation'}
                </p>
              </div>
            ) : calculationResult.suggestions.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'zh' ? '沒有找到符合條件的產品' : 'No products found matching the criteria'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/60">
                    <TableRow>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品ID' : 'Product ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '成本' : 'Cost'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '倉庫' : 'Warehouse'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '狀態' : 'Status'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '現存量' : 'Current Stock'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '安全量' : 'Safety Stock'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '建議採納量' : 'Suggested Quantity'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calculationResult.suggestions.map((item, idx) => (
                      <TableRow key={item.productId} className={`${idx % 2 === 0 ? 'bg-card/40' : ''} hover:bg-accent/30`}>
                        <TableCell className="text-blue-400 font-medium">
                          {item.productId}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.brand}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-xs">
                          <div className="line-clamp-2">
                            {item.productName}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono">
                          {item.cost > 0 ? `$${item.cost.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.warehouse}
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'ONLINE' ? 'default' : 'secondary'}>
                            {item.status === 'ONLINE'
                              ? (language === 'zh' ? '在線' : 'Online')
                              : (language === 'zh' ? '離線' : 'Offline')
                            }
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono">
                          {item.currentStock.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono">
                          {Math.ceil(item.safetyStock).toLocaleString()}
                        </TableCell>
                        <TableCell className={`font-mono font-semibold ${
                          item.suggestedQuantity > 0
                            ? 'text-green-400'
                            : item.suggestedQuantity < 0
                              ? 'text-red-400'
                              : 'text-muted-foreground'
                        }`}>
                          {Math.ceil(item.suggestedQuantity) > 0 ? '+' : ''}{Math.ceil(item.suggestedQuantity).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CrossCompanySuggestions;

